import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import { FaKey } from "react-icons/fa";

const apiUrl = process.env.REACT_APP_API_URL;

const UserAddPage = () => {
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({
    roleId: "",
    username: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  // Fetch roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axiosInstance.get(`${apiUrl}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoles(response.data.roles || []);
      } catch (err) {
        toast.error("Failed to load roles");
      }
    };
    fetchRoles();
  }, []);

  const generatePassword = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let generatedPassword = "";
    for (let i = 0; i < 10; i++) {
      generatedPassword += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setPassword(generatedPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ roleId: "", username: "", email: "", password: "" });

    if (!roleId || !username || !email || !password) {
      setErrors((prev) => ({
        ...prev,
        roleId: !roleId ? "Role is required" : "",
        username: !username ? "Username is required" : "",
        email: !email ? "Email is required" : "",
        password: !password ? "Password is required" : "",
      }));
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      await axiosInstance.post(
        `${apiUrl}/user/add`,
        { roleId, username, email, password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("User registered successfully!");
      setUsername("");
      setEmail("");
      setPassword("");
      setRoleId("");
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ bgcolor: "#f7f7f7", p: 3 }}
    >
      <Typography variant="h4" gutterBottom>
        User Register
      </Typography>

      <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 500 }}>
        <FormControl fullWidth margin="normal" error={Boolean(errors.roleId)}>
          <InputLabel>Select Role</InputLabel>
          <Select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            label="Select Role"
          >
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.roleId}</FormHelperText>
        </FormControl>

        <TextField
          label="Username"
          variant="outlined"
          margin="normal"
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          error={Boolean(errors.username)}
          helperText={errors.username}
        />
        <TextField
          label="Email"
          variant="outlined"
          margin="normal"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
        <TextField
          label="Password"
          type="text"
          variant="outlined"
          margin="normal"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={Boolean(errors.password)}
          helperText={errors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={generatePassword} edge="end">
                  <FaKey />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Register"}
        </Button>
      </form>
    </Box>
  );
};

export default UserAddPage;
